import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    CheckCircleOutline,
    LocalShipping,
    AccessTime,
    AttachMoney,
    Star,
    Home,
    People,
    KeyboardArrowRight,
    ThumbUpAlt,
    InfoOutlined,
    LocalOffer,
    TrendingUp,
    VerifiedUser,
    MenuBook
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: '家具レンタルがルームシェアに人気の理由' },
    { id: 'merits', label: '家具レンタルの主なメリット' },
    { id: 'comparison', label: 'おすすめ家具レンタルサービス比較' },
    { id: 'voices', label: '利用者のリアルな声' },
    { id: 'choose', label: '家具レンタルサービス選びのポイント' },
    { id: 'faq', label: 'よくある質問とアドバイス' },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem
                                    key={item.id}
                                    sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}
                                >
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家具レンタルがルームシェアに人気の理由
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、<b>ルームシェア</b>という新しい住まい方が若者を中心に広がっています。その背景には、<b>初期費用を抑えたい</b>、<b>柔軟に暮らしたい</b>というニーズの高まりがあります。そんなルームシェア生活で注目されているのが、<b>家具レンタルサービス</b>です。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「費用を節約しながら、おしゃれな暮らしも妥協したくない」「短期間だけ家具が必要」「引っ越しや同居解消時の手間を減らしたい」――そんなルームシェアの悩みを解決してくれるのが、家具レンタル。今回は、家具レンタルのメリットや主要サービスの比較、実際に利用した方の声まで、専門的な視点から徹底解説します。
                </Typography>
                <Box sx={{ my: 3, textAlign: 'center' }}>

                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        共有リビングにぴったりな家具レンタルで、ルームメイトとの暮らしも快適に
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* メリット */}
            <Box id="merits" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家具レンタルの主なメリット
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AttachMoney color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>初期費用を大幅カット</Typography>
                            </Box>
                            <Typography variant="body2">
                                家具を一式購入すると、ルームシェア開始時に数万円〜十数万円の出費が必要です。家具レンタルなら、月額数千円から必要な分だけ借りられるので、<b>コストを分散</b>できます。
                            </Typography>
                        </Paper>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocalShipping color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>引っ越し・処分がラク</Typography>
                            </Box>
                            <Typography variant="body2">
                                レンタルなら、退去時の家具回収もサービス側が対応。<b>粗大ごみの手配や廃棄費用が不要</b>なので、忙しい引っ越し時にも安心です。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AccessTime color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>期間・人数に合わせて柔軟に</Typography>
                            </Box>
                            <Typography variant="body2">
                                ルームメイトの入れ替えや滞在期間の変化にも、<b>必要な家具だけを短期・長期で借りられる</b>のが強み。生活スタイルが変わってもフレキシブルに対応できます。
                            </Typography>
                        </Paper>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Star color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>おしゃれで高品質な家具が使える</Typography>
                            </Box>
                            <Typography variant="body2">
                                人気ブランドやトレンドデザインの家具も豊富。<b>自分たちでは手が届かない上質な家具</b>を気軽に取り入れることができます。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                        他にも注目ポイント
                    </Typography>
                    <List>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="故障時も修理・交換対応がスムーズ" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="配置やサイズの相談もできるカスタマーサポート" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="ルームシェア解消時のトラブル（誰が家具を引き取るか等）を防げる" />
                        </ListItem>
                    </List>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* サービス比較 */}
            <Box id="comparison" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    おすすめ家具レンタルサービス比較
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ここからは、<b>ルームシェアにおすすめの主要家具レンタルサービス</b>を比較します。それぞれの特徴や料金、ルームシェア向けの強みをまとめました。
                </Typography>

                <Grid container spacing={2}>
                    {/* サービス１ */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ maxWidth: 345, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Home color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        CLAS
                                    </Typography>
                                </Box>
                                <Chip label="月額440円〜" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                                <Chip label="1ヶ月〜" color="success" variant="outlined" sx={{ mb: 1 }} />
                                <Typography variant="body2">
                                    コスパ抜群の家具・家電サブスク。プランによる最低利用期間なしで、好きな月に返却可能。<br />
                                    <b>ブランド家具をリーズナブルにお試し</b>でき、長期利用で割安に。<br />
                                </Typography>
                                <List sx={{ mt: 1 }}>
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemIcon><ThumbUpAlt color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="往復送料・返却手数料プランで無料" />
                                    </ListItem>
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemIcon><ThumbUpAlt color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="レンタル料金を購入価格に充当可能" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* サービス２ */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ maxWidth: 345, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <People color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        subsclife
                                    </Typography>
                                </Box>
                                <Chip label="月額500円〜" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                                <Chip label="3ヶ月〜" color="info" variant="outlined" sx={{ mb: 1 }} />
                                <Typography variant="body2">
                                    ブランド家具を定額レンタル。<b>3ヶ月から利用可能</b>で、途中解約はレンタル期間満了後に無料。<br />
                                    気に入れば購入も可能、破損保証付きで安心です。
                                </Typography>
                                <List sx={{ mt: 1 }}>
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemIcon><ThumbUpAlt color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="商品破損補償付き" />
                                    </ListItem>
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemIcon><ThumbUpAlt color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="利用後購入可能" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* サービス３ */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ maxWidth: 345, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TrendingUp color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        airRoom
                                    </Typography>
                                </Box>
                                <Chip label="月額500円〜" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                                <Chip label="1ヶ月〜12ヶ月" color="warning" variant="outlined" sx={{ mb: 1 }} />
                                <Typography variant="body2">
                                    人気家具ブランドを月額利用。<b>最短1ヶ月から</b>利用でき、プロのインテリアコーディネート無料。<br />
                                    気に入れば購入も可能で、返却・交換も無料サポート。
                                </Typography>
                                <List sx={{ mt: 1 }}>
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemIcon><ThumbUpAlt color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="プロコーディネート無料" />
                                    </ListItem>
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemIcon><ThumbUpAlt color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="返却・交換手数料無料" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ my: 3, textAlign: 'center' }}>

                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        各サービスでシェア暮らしに合わせた家具を自由に選択可能
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    ※料金やサービス内容は2024年6月時点の情報です。最新情報は各公式サイトでご確認ください。
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 利用者の声 */}
            <Box id="voices" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    利用者のリアルな声
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/user1.jpg" alt="ルームシェア利用者A" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>社会人2人暮らし（20代女性）</Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>「初期費用が抑えられて助かった」</b>
                                <br />
                                ソファやテーブルをCLASでレンタルしました。最初に大きな出費がないので、生活を始めやすかったです。<br />
                                退去時も回収してくれるので、手間がかからず便利！
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/user2.jpg" alt="ルームシェア利用者B" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>学生グループ3人（男性）</Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>「みんなで費用分担できて納得」</b>
                                <br />
                                家電も含めてまとめてレンタル。誰が家具を引き取るかで揉めることもなく、スムーズにシェア解消できました。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            <InfoOutlined color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                            こんな声も
                        </Typography>
                        <List>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemIcon><Star color="warning" /></ListItemIcon>
                                <ListItemText primary="「好みの家具を選べてインテリアも充実」" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemIcon><Star color="warning" /></ListItemIcon>
                                <ListItemText primary="「急な転勤や入れ替え時も対応が早い」" />
                            </ListItem>
                        </List>
                    </Paper>
                </Box>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/roommate-unpacking_roomshare-osusume-kagu-rental-hikaku.jpg"
                        alt="明るいリビングでルームメイト2人が新しいレンタル家具を開封している様子。ナチュラルウッドのテーブルや白いソファが部屋に運び込まれ、楽しそうな雰囲気が広がっている。"
                        style={{ maxWidth: '100%', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        届いた家具をルームメイトみんなで配置。新生活のワクワク感もシェアできる
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 選び方のポイント */}
            <Box id="choose" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家具レンタルサービス選びのポイント
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        ルームシェアで家具レンタルを選ぶ際は、<b>以下の点をチェック</b>しましょう。
                    </Typography>
                    <List>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><LocalOffer color="secondary" /></ListItemIcon>
                            <ListItemText primary="最低利用期間と解約条件を確認" secondary="短期のみOKか、途中解約の違約金は要注意" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><VerifiedUser color="primary" /></ListItemIcon>
                            <ListItemText primary="修理・交換対応やサポート体制" secondary="故障・破損時の対応が安心できるか" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><Home color="primary" /></ListItemIcon>
                            <ListItemText primary="配送・回収エリア" secondary="対応地域か・送料や設置費用の有無も要チェック" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon><MenuBook color="success" /></ListItemIcon>
                            <ListItemText primary="インテリアのテイストや品揃え" secondary="好みや間取りに合う家具を選びやすいか" />
                        </ListItem>
                    </List>
                </Box>
                <Paper elevation={1} sx={{ p: 2, borderLeft: '6px solid #1976d2', bgcolor: '#f4faff', borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        専門家からのアドバイス
                    </Typography>
                    <Typography variant="body2">
                        家具レンタルは、<b>「みんなで使う家具」と「個人で使う家具」を分けて借りる</b>のがコツ。<br />
                        費用分担や返却時のトラブルも起こりにくく、ルームシェア生活がより快適になります。<br />
                        また、<b>契約者名義</b>や<b>支払い方法</b>も事前に相談しておくと安心です。
                    </Typography>
                </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* FAQ・アドバイス */}
            <Box id="faq" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    よくある質問とアドバイス
                </Typography>
                <List>
                    <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                        <ListItemIcon>
                            <InfoOutlined color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Q. ルームシェアの人数が変わったら契約内容も変更できる？</Typography>}
                            secondary="A. 多くのサービスで途中変更・追加レンタルが可能です。人数増減に応じて柔軟に相談しましょう。"
                        />
                    </ListItem>
                    <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                        <ListItemIcon>
                            <InfoOutlined color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Q. 汚れや傷がついた場合は？</Typography>}
                            secondary="A. 通常使用の範囲なら原則補償内ですが、破損や汚損が大きい場合は費用が発生することも。各サービスの補償内容を事前にチェックしましょう。"
                        />
                    </ListItem>
                    <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                        <ListItemIcon>
                            <InfoOutlined color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Q. 家具の返却時はどうする？</Typography>}
                            secondary="A. 希望日時を指定してサービス側が回収に来てくれるので、梱包や運搬の手間は不要です。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/shared-room-clean-modern_roomshare-osusume-kagu-rental-hikaku.jpg"
                        alt="清潔感のあるモダンな共有リビングで、シンプルなレンタル家具が設置されている様子。白と木目調のインテリアに観葉植物が映え、整然とした空間が広がる。新しい生活をイメージしやすい雰囲気。"
                        style={{ maxWidth: '100%', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        綺麗なレンタル家具で快適なルームシェア生活を実現
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-osusume-kagu-rental-hikaku',
    slug: 'roomshare-osusume-kagu-rental-hikaku',
    title: 'ルームシェアの「おすすめ家具レンタル」サービス比較',
    excerpt: '家具レンタルのメリット、主要サービスの比較、利用者の声、選び方のポイントをまとめて紹介します。',
    coverImage: '/images/blog/cover-share-living-light_roomshare-osusume-kagu-rental-hikaku.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。白基調の空間にカラフルな家具が調和し、リラックスした雰囲気が伝わる。',
    publishedAt: '2024-06-18T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '家具レンタル', '生活ノウハウ'],
    tags: ['家具レンタル', 'ルームシェア', 'サービス比較'],
    author: {
        name: '三浦 直子',
        avatar: '/images/avatars/miura-naoko.jpg',
        bio: 'シェアハウスや賃貸住まいの暮らしをサポートする住宅ライター。数多くのルームシェア物件と家具レンタルサービスを取材し、実体験に基づいたアドバイスが得意。',
        profilePicture: '/images/avatars/miura-naoko.jpg'
    },
    viewCount: 1260,
    contentComponent: PostContent
};

export default post;